import { Ref, forwardRef } from "react"
import { Form, Formik, FormikProps, useFormikContext } from "formik"
import * as yup from "yup"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/pro-light-svg-icons"
import { Box, Grid, InputAdornment, MenuItem, TextField, Typography } from "@mui/material"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { NumberFormatCustom } from "../../../../../merchant/admin/products/components/FormikCurrencyField"
import { ConfigureLanguage, PosPrintingMode, UpdateGeneralSettings } from "../supplierPosSettingsTypes"
import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { FormikDropdown } from "../../../../../../shared/components/forms/Formik"
import { selectUserType } from "../../../../../login/loginSelectors"
import { useAppSelector } from "../../../../../store/storeHooks"
import { iterateStringEnum } from "../../../../../../lib/enums"
import { UserType } from "../../../../../login/loginTypes"
import { LanguageCode } from "../../termsAndConditions/termsAndConditionTypes"

type UpdateGeneralSettingsFormProps = Readonly<{
    initialValues?: UpdateGeneralSettings | null
    onSubmit: (formData: UpdateGeneralSettings) => void
}>
type FormikRef = Ref<FormikProps<UpdateGeneralSettings>>

export const UpdateGeneralSettingsForm = forwardRef(
    ({ initialValues, onSubmit }: UpdateGeneralSettingsFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()
        const actualUserType = useAppSelector(selectUserType)
        const isUserTypeSupport = actualUserType === UserType.SUPPORT

        const defaultInitialValues = {
            localization: {
                defaultLanguage: LanguageCode.DA,
            },
            receipt: {
                posPrintingMode: PosPrintingMode.AUTO,
            },
            interaction: {
                returnToHomeScreenDelay: 3000,
                returnToHomeAfterPurchase: 3000,
            },
        }

        const validationSchema = yup.object({
            localization: yup.object().shape({
                defaultLanguage: yup.string().required(l10n.getString("required-field")),
            }),
            receipt: yup.object().shape({
                posPrintingMode: yup.string().required(l10n.getString("required-field")),
            }),
        })

        function handleSubmit(values: UpdateGeneralSettings) {
            const { interaction, ...templateValues } = values

            const modifyData = {
                ...templateValues,
                ...(isUserTypeSupport ? interaction : {}),
            }
            onSubmit(modifyData)
        }

        return (
            <>
                <Formik<UpdateGeneralSettings>
                    enableReinitialize
                    initialValues={initialValues ? initialValues : defaultInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    innerRef={ref}
                >
                    <Form>
                        <UpdateGeneralSettingsFormContent isUserTypeSupport={isUserTypeSupport} />
                    </Form>
                </Formik>
            </>
        )
    },
)

const useStyles = makeStyles()((theme) => ({
    container: {
        gap: "1.5rem",
        flexWrap: "nowrap",
    },
    boxWrap: {
        border: `1px solid ${theme.palette.tailwind.slate[100]}`,
        borderRadius: "12px",
        boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.08)",
        background: "white",
    },
    boxColl: {
        maxWidth: "20rem",
    },
    infoWrap: {
        display: "grid",
        gridTemplateColumns: "0.75rem 1fr",
        background: theme.palette.tailwind.sky[100],
        borderRadius: "0.5rem",
        gap: "0.75rem",
        padding: "0.75rem",
        color: theme.palette.tailwind.sky[800],
        width: "100%",
    },
    configurationTitle: {
        padding: "0.75rem 1.25rem",
        color: theme.palette.tailwind.slate[600],
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
}))

type UpdateGeneralSettingsFormContentProps = Readonly<{
    isUserTypeSupport: boolean
}>

export function UpdateGeneralSettingsFormContent({ isUserTypeSupport }: UpdateGeneralSettingsFormContentProps) {
    const { values, setFieldValue } = useFormikContext<UpdateGeneralSettings>()
    const { classes, cx } = useStyles()

    return (
        <>
            <Grid className={classes.container}>
                <Grid item xs={9}>
                    <Grid container flexDirection="column" gap={1.25} padding="2rem" className={cx(classes.boxWrap)}>
                        <Grid container flexDirection="column" gap={4} className={classes.boxColl}>
                            <LocalizedStrict
                                id="supplier-admin-pos-settings-general-defaultLanguage-field-label"
                                attrs={{ label: true, helperText: true }}
                            >
                                <FormikDropdown name="localization.defaultLanguage" label="Default language">
                                    {[...iterateStringEnum(ConfigureLanguage)].map((code) => (
                                        <MenuItem key={code} value={code}>
                                            <LocalizedEnum base="supplier-admin-pos-settings-lang" enumValue={code}>
                                                <span>{code}</span>
                                            </LocalizedEnum>
                                        </MenuItem>
                                    ))}
                                </FormikDropdown>
                            </LocalizedStrict>
                            <LocalizedStrict
                                id="supplier-admin-pos-settings-general-posPrintingMode-field-label"
                                attrs={{ label: true }}
                            >
                                <FormikDropdown name="receipt.posPrintingMode" label="Receipt print mode">
                                    {[...iterateStringEnum(PosPrintingMode)].map((mode) => (
                                        <MenuItem key={mode} value={mode}>
                                            <LocalizedEnum
                                                base="supplier-admin-pos-settings-general-posPrintingMode-option"
                                                enumValue={mode}
                                            >
                                                <span>{mode}</span>
                                            </LocalizedEnum>
                                        </MenuItem>
                                    ))}
                                </FormikDropdown>
                            </LocalizedStrict>
                        </Grid>

                        <Box className={classes.infoWrap}>
                            <FontAwesomeIcon size="sm" icon={faCircleInfo} />
                            <LocalizedStrict id="supplier-admin-pos-settings-general-info">
                                <Typography variant="caption">
                                    The print mode controls how the receipt is made available to the customer.
                                    “Automatic print” will print a paper receipt automatically whereas “Manual print”
                                    allows the user to print paper receipt or scan QR code to get a digital receipt.
                                    Choose “QR code only” if you want a paperless POS system.
                                </Typography>
                            </LocalizedStrict>
                        </Box>
                    </Grid>
                    {isUserTypeSupport && (
                        <Grid container flexDirection="column" gap={1.25} marginTop={3} className={cx(classes.boxWrap)}>
                            <LocalizedStrict id="supplier-admin-pos-settings-general-configuration-for-supporters">
                                <Typography variant="subtitle1" fontWeight={600} className={classes.configurationTitle}>
                                    Configuration for supporters
                                </Typography>
                            </LocalizedStrict>
                            <Grid
                                container
                                flexDirection="column"
                                padding="1.75rem 1.25rem"
                                gap={3}
                                className={cx(classes.boxColl)}
                            >
                                <LocalizedStrict
                                    id="supplier-admin-pos-settings-general-configuration-for-supporters-returnToHomeScreenDelay-field-label"
                                    attrs={{ label: true }}
                                >
                                    <TextField
                                        label="Return to home screen delay"
                                        name="interaction.returnToHomeScreenDelay"
                                        value={values?.interaction?.returnToHomeScreenDelay}
                                        onChange={(e) => {
                                            setFieldValue("interaction.returnToHomeScreenDelay", Number(e.target.value))
                                        }}
                                        InputProps={{
                                            inputComponent: NumberFormatCustom as any,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <LocalizedStrict id="supplier-admin-pos-settings-general-configuration-for-supporters-ms">
                                                        <span>ms</span>
                                                    </LocalizedStrict>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </LocalizedStrict>
                                <LocalizedStrict
                                    id="supplier-admin-pos-settings-general-configuration-for-supporters-returnToHomeAfterPurchase-field-label"
                                    attrs={{ label: true }}
                                >
                                    <TextField
                                        label=" Return to home after purchase"
                                        name="interaction.returnToHomeAfterPurchase"
                                        value={values?.interaction?.returnToHomeAfterPurchase}
                                        onChange={(e) => {
                                            setFieldValue(
                                                "interaction.returnToHomeAfterPurchase",
                                                Number(e.target.value),
                                            )
                                        }}
                                        InputProps={{
                                            inputComponent: NumberFormatCustom as any,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <LocalizedStrict id="supplier-admin-pos-settings-general-configuration-for-supporters-ms">
                                                        <span>ms</span>
                                                    </LocalizedStrict>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </LocalizedStrict>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </>
    )
}
